import type { useCurrentUser } from '@/features/auth/hooks/useAuthMutations';
import { hasPermission, PERMS } from '@/lib/permissions';

/**
 * Sidebar nav configuration — single source of truth for the menu.
 *
 * Sprint 1 WU-1 refactor. The flat `NAV_ITEMS` array now lives
 * inside a `NAV_GROUPS` array of 5 groups:
 *
 *     general → ventas → operaciones → finanzas → sistema
 *
 * Each `NavItem` keeps the same surface (`to`, `label`, `iconName`,
 * optional `requiredPermission`, optional `end`). The iconName union
 * is extended with six new lucide-react icon names that the
 * grouped sidebar needs:
 *
 *   - bell         → Notificaciones (sidebar badge target)
 *   - receipt      → Tasas BCV chip route
 *   - building-2   → Compras (PURCHASES group icon)
 *   - layout-grid  → reserved for future Dashboard grid
 *   - briefcase    → CRM (ventas)
 *   - piggy-bank   → reserved for future Caja/Bancos entry
 *
 * `visibleNavItems(user)` preserves its existing public contract
 * (it returns a flat `readonly NavItem[]`) so callers like
 * `Sidebar.tsx` can iterate without caring about groups. The new
 * grouping info is exposed separately via `NAV_GROUPS` for the
 * render loop; groups with no items after the permission filter
 * are rendered as nothing (REQ-1 empty-group rule).
 */

export type IconName =
  | 'layout-dashboard'
  | 'users'
  | 'package'
  | 'shopping-cart'
  | 'bar-chart-3'
  | 'settings'
  | 'warehouse'
  | 'truck'
  | 'shield-check'
  | 'file-check'
  // Sprint 1 extensions — see header for rationale
  | 'bell'
  | 'receipt'
  | 'building-2'
  | 'layout-grid'
  | 'briefcase'
  | 'piggy-bank'
  // Sprint 3 PR#3a — Roles icon
  | 'key-round'
  // Sprint 3 PR#3b — Auditoría icon (the `audit.view` permission
  // constant lives at `PERMS.AUDIT.VIEW` in
  // `@repo/shared-types/permissions`).
  | 'history'
  // HR expediente — Trabajadores entry (Sistema group).
  | 'id-card';

export interface NavItem {
  to: string;
  label: string;
  iconName: IconName;
  requiredPermission?: Parameters<typeof hasPermission>[1];
  end?: boolean;
}

export type NavGroupId = 'general' | 'ventas' | 'operaciones' | 'finanzas' | 'sistema';

export interface NavGroup {
  id: NavGroupId;
  /** Uppercase section header rendered between groups. */
  label: string;
  items: readonly NavItem[];
}

/**
 * Group order is contractual: spec REQ-1 requires this exact order:
 * General → Ventas → Operaciones → Finanzas → Sistema.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    id: 'general',
    label: 'General',
    items: [
      { to: '/', label: 'Panel', iconName: 'layout-dashboard', end: true },
      // Visible to all authenticated users — matches the topbar
      // NotificationBell (which is also unconditionally mounted).
      // The sidebar badge is the discovery cue; the item itself
      // has no permission gate.
      { to: '/notifications', label: 'Notificaciones', iconName: 'bell' },
    ],
  },
  {
    id: 'ventas',
    label: 'Ventas',
    items: [
      {
        to: '/crm',
        label: 'CRM',
        iconName: 'briefcase',
        requiredPermission: PERMS.CRM.VIEW_CLIENTS,
      },
      {
        // Sprint 2 PR#S2b (A3) — CRM analytics dashboard. Route
        // `/crm/analytics` is lazy-loaded in router.tsx and the
        // page lives in `apps/web/src/features/crm/pages/CrmAnalyticsPage.tsx`.
        // Reuses CRM.VIEW_CLIENTS because the analytics are a
        // read-only projection of CRM data; WAREHOUSE (no CRM
        // permission) is filtered out automatically by `filterVisible`.
        to: '/crm/analytics',
        label: 'Analíticas CRM',
        iconName: 'bar-chart-3',
        requiredPermission: PERMS.CRM.VIEW_CLIENTS,
      },
      {
        // Slice 2 — seller follow-up queue (`/crm/tasks`). Gated by
        // the granular `crm.manage_tasks` key (SELLER + ADMIN);
        // WAREHOUSE is filtered out automatically by `filterVisible`.
        to: '/crm/tasks',
        label: 'Tasks',
        iconName: 'briefcase',
        requiredPermission: PERMS.CRM.MANAGE_TASKS,
      },
      {
        to: '/quotations',
        label: 'Cotizaciones',
        iconName: 'file-check', // matches the existing union; file-text icon is a WU-1+ follow-up
        requiredPermission: PERMS.SALES.VIEW_QUOTATIONS,
      },
      {
        to: '/sales-orders',
        label: 'Órdenes',
        iconName: 'shopping-cart',
        requiredPermission: PERMS.SALES.VIEW_ORDERS,
      },
    ],
  },
  {
    id: 'operaciones',
    label: 'Operaciones',
    items: [
      {
        to: '/inventory',
        label: 'Inventario',
        iconName: 'package',
        requiredPermission: PERMS.INVENTORY.VIEW,
      },
      {
        to: '/dispatches',
        label: 'Despachos',
        iconName: 'truck',
        requiredPermission: PERMS.DISPATCH.VIEW,
      },
      {
        to: '/warehouse',
        label: 'Bandeja de depósito',
        iconName: 'warehouse',
        requiredPermission: PERMS.DISPATCH.VIEW,
      },
      {
        // Sprint 1 B3 — purchases moved under /purchases; the
        // sidebar exposes the consolidated entry here so WAREHOUSE
        // and ADMIN can reach the consolidated POs.
        to: '/purchases',
        label: 'Compras',
        iconName: 'building-2',
        requiredPermission: PERMS.PURCHASES.VIEW,
      },
      {
        // Sprint 2b — kpis-de-compras REQ-A1. Flat sibling of
        // "Compras" in the Operaciones group, gated by purchases.view
        // so WAREHOUSE/SELLER/ADMIN all see it (same permission as
        // Compras). The route is wired in router.tsx:441-447 and
        // resolves to apps/web/src/features/purchases/pages/
        // PurchaseAnalyticsPage.tsx. Icon matches Analíticas CRM +
        // Reportes (the existing convention for analytics entries).
        to: '/purchases/analytics',
        label: 'KPIs de Compras',
        iconName: 'bar-chart-3',
        requiredPermission: PERMS.PURCHASES.VIEW,
      },
    ],
  },
  {
    id: 'finanzas',
    label: 'Finanzas',
    items: [
      {
        to: '/finance/reports',
        label: 'Reportes',
        iconName: 'bar-chart-3',
        requiredPermission: PERMS.REPORTS.VIEW,
      },
      {
        // BCV rates — open to all authenticated users; the
        // destination is the ADMIN management page but the read
        // info (current USD/VES) is also surfaced by the
        // ExchangeRateBadge chip in the TopBar.
        to: '/exchange-rates',
        label: 'Tasas BCV',
        iconName: 'receipt',
      },
      // sdd/finanzas-modulo PR#1 — top-level Finanzas index. The
      // route is mounted at `/finance` (router.tsx adds the child
      // routes in PR#3) and is gated by the new
      // `finance.view_finance_module` permission (PR#1 of the
      // change). SELLER/WAREHOUSE lack the perm and are filtered
      // out automatically; ADMIN gets it via ALL_PERMISSION_VALUES.
      {
        to: '/finance',
        label: 'Finanzas',
        iconName: 'piggy-bank',
        requiredPermission: PERMS.FINANCE.VIEW_FINANCE_MODULE,
        end: true,
      },
      // sdd/finanzas-modulo PR#1 — Patrimonio (equity) entry. The
      // route is mounted at `/finance/equity` in PR#3. The perm
      // `finance.view_equity` was already in the catalog (Slice
      // 3) and the existing EquityPage renders under it; this
      // commit only adds the sidebar wiring.
      {
        to: '/finance/equity',
        label: 'Patrimonio',
        iconName: 'piggy-bank',
        requiredPermission: PERMS.FINANCE.VIEW_EQUITY,
      },
    ],
  },
  {
    id: 'sistema',
    label: 'Sistema',
    items: [
      {
        // Client request — the catalog module existed only as routes
        // (/catalog, /catalog/products, ...) with no sidebar entry,
        // so operators could not discover it (the "Productos
        // vendidos" picker in Finanzas reads GET /products directly,
        // which is why that dropdown worked while the module stayed
        // invisible). Gated by catalog.view_products, held by
        // ADMIN/SELLER/WAREHOUSE; the /catalog page itself renders
        // an access fallback without it. Placed in Sistema (master
        // data) so the per-role shapes asserted in nav-items.test.ts
        // for Ventas/Operaciones stay unchanged.
        to: '/catalog',
        label: 'Catálogo',
        iconName: 'package',
        requiredPermission: PERMS.CATALOG.VIEW_PRODUCTS,
      },
      {
        to: '/users',
        label: 'Usuarios',
        iconName: 'users',
        requiredPermission: PERMS.USERS.VIEW,
      },
      {
        // HR expediente — ADMIN-only via `hr.view_workers`; SELLER /
        // WAREHOUSE are filtered out so the Sistema group keeps its
        // per-role shape. Route lives at
        // `apps/web/src/features/workers/` (lazy-loaded in router.tsx).
        to: '/workers',
        label: 'Trabajadores',
        iconName: 'id-card',
        requiredPermission: PERMS.HR.VIEW_WORKERS,
      },
      {
        // Slice 8 — every authenticated user can see the inbox
        // (the route is gated by authorizations.view); the per-row
        // mutation buttons are gated by their respective permissions
        // (authorizations.create / authorizations.respond).
        to: '/authorizations',
        label: 'Autorizaciones',
        iconName: 'shield-check',
        requiredPermission: PERMS.AUTHORIZATIONS.VIEW,
      },
      {
        // Sprint 1 B4 — repointed from /settings. Permission
        // changed from TENANTS.UPDATE to NOTIFICATIONS.VIEW since
        // the destination is a per-user preferences page, not a
        // tenant settings admin.
        to: '/settings/notifications',
        label: 'Configuración',
        iconName: 'settings',
        requiredPermission: PERMS.NOTIFICATIONS.VIEW,
      },
      {
        // Sprint 3 PR#3a — read-only role catalogue page. ADMIN-only
        // via `roles.view`; SELLER/WAREHOUSE filtered out so
        // the Sistema group shows the correct shape per role. The
        // route feature folder lives at
        // `apps/web/src/features/roles/` and is lazy-loaded by
        // `apps/web/src/router.tsx`.
        to: '/roles',
        label: 'Roles y Permisos',
        iconName: 'key-round',
        requiredPermission: PERMS.ROLES.VIEW,
      },
      {
        // Sprint 3 PR#3b — ADMIN-only audit log page. The
        // `audit.view` permission is defined in
        // `@repo/shared-types/permissions` and re-exported via
        // `PERMS` from `@/lib/permissions`. Replaced the
        // literal-string cast (left over from PR#3a) with the
        // proper constant so the type system enforces the leaf
        // permission contract.
        to: '/audit',
        label: 'Auditoría',
        iconName: 'history',
        requiredPermission: PERMS.AUDIT.VIEW,
      },
    ],
  },
];

/**
 * Flat visible nav items — kept as a memoized flat list for
 * `Sidebar.tsx` and any future caller (mobile drawer, search
 * palette, etc.) that doesn't want to walk the group tree.
 *
 * The permission filter is applied per-item so a group may end up
 * empty after the filter; that is the sidebar's responsibility
 * to handle (it skips empty groups).
 */
function filterVisible(
  items: readonly NavItem[],
  user: NonNullable<ReturnType<typeof useCurrentUser>>,
): NavItem[] {
  return items.filter(
    (item) => !item.requiredPermission || hasPermission(user.permissions, item.requiredPermission),
  );
}

export function visibleNavItems(user: ReturnType<typeof useCurrentUser>): readonly NavItem[] {
  if (!user) return [];
  const out: NavItem[] = [];
  for (const group of NAV_GROUPS) {
    out.push(...filterVisible(group.items, user));
  }
  return out;
}

/**
 * Visible items grouped — used by `Sidebar.tsx` to render the
 * section headers and skip empty groups in a single pass.
 */
export function visibleNavGroups(
  user: ReturnType<typeof useCurrentUser>,
): readonly { id: NavGroupId; label: string; items: readonly NavItem[] }[] {
  if (!user) return [];
  const out: { id: NavGroupId; label: string; items: readonly NavItem[] }[] = [];
  for (const group of NAV_GROUPS) {
    const items = filterVisible(group.items, user);
    if (items.length === 0) continue; // REQ-1: skip empty groups
    out.push({ id: group.id, label: group.label, items });
  }
  return out;
}
